import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { DestroyBase } from 'src/app/components/common/ng-destroy-base';
import { CalculationModelOverview } from '../../../models/calculation-model-overview';
import { CalculationModelService } from '../../../services/calculation-model-service';
import { DataBindingDirective, SelectableSettings, SelectionEvent } from '@progress/kendo-angular-grid';
import { GenericPolluxResult } from '../../../../../models/generic-pollux-result';
import { EventBusService } from '../../../../../services/event-bus.service';

/**
 * Handles calculation model assign component
 */
@Component({
  selector: 'calculation-model-assign',
  templateUrl: './calculation-model-assign.component.html',
  styleUrls: ['./calculation-model-assign.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CalculationModelAssignComponent extends DestroyBase implements OnInit {
  /**
  * Input property for the object ID.
  */
  @Input() oid: number;
  /**
   * ViewChild property for the DataBindingDirective.
   */
  @ViewChild(DataBindingDirective) dataBinding: DataBindingDirective;

  /**
   * Array to hold calculation model overview models.
   */
  models: CalculationModelOverview[] = [];

  /**
   * Array to store the grid view of calculation model overview.
   */
  public gridView: CalculationModelOverview[];

  /**
   * Selectable settings for the grid.
   */
  public selectableSettings: SelectableSettings = {
    checkboxOnly: false,
    mode: 'single',
    drag: false,
  };

  /**
  * Array to store the selected IDs.
  */
  public selectedIds: number[] = [];

  /**
   * Flag to indicate if the component should close.
   */
  shouldClose = false;

  /**
   * The ID of the project.
   */
  projectId = 0;

  /**
   * Array to store the property IDs.
   */
  propertyIds = [];

  /**
   * Constructor
   * @param cmService
   * @param ref
   */
  constructor(private cmService: CalculationModelService, private eventBus: EventBusService, private ref: ChangeDetectorRef) {
    super();
  }

  /**
   * Lifecycle hook that is called after the component's data-bound properties have been initialized.
   */
  ngOnInit(): void {
    this.subscriptions.add(this.cmService.getCalculationModels().subscribe(data => {
      this.gridView = data.filter(o => o.statusId === 0);
      this.models = data;
      this.ref.markForCheck();
    }));

    this.projectId = this.oid;
    this.propertyIds = [];
    this.eventBus.broadcastEvent('properties:observeCheckedProperties', this.observePropertiesCallback.bind(this));
  }

  /**
   * Close clicked.
   */
  closeClicked() {
    this.shouldClose = true;
    setTimeout(() => {
      this.oid = 0;
      this.eventBus.broadcastEvent('properties:observeCheckedProperties', null);
    }, 500);//let animation finish
  }

  /**
   * Selection event.
   */
  onSelect(_e: SelectionEvent) {
    //Nothing
  }

  /**
   *  Checks already assigned properties.
   */
  assignTemplateToProperties(checkAlreadyAssigned) {
    const data = {
      calculationModelId: this.selectedIds[0],
      projectId: this.projectId,
      propertyIds: this.propertyIds
    };

    if (checkAlreadyAssigned) {
      this.cmService.assignCalculationModelCheck(data).subscribe({
        next: (gpr: GenericPolluxResult) => {
          if (!gpr.success) {
            // some properties already have some other template assigned
            this.showReplaceTemplateDialog();
          } else {
            this.assignTemplateToProperties(false);
          }
        },
        error: (_reason) => {
          this.showTemplateAppliedDialog(false);
        }
      });
    } else {
      this.cmService.assignCalculationModel(data).subscribe({
        next: (gpr: GenericPolluxResult) => {
          if (gpr.success) {
            this.eventBus.broadcastEvent('properties:calculationModelTemplateAssigned', this.projectId);
          }
          this.showTemplateAppliedDialog(gpr.success);
        },
        error: (_reason) => {
          this.showTemplateAppliedDialog(false);
        }
      });
    }
  }

  /**
   *  Apply to the properties.
   */
  applyToProperties() {
    this.eventBus.broadcastEvent('properties:getCheckedProperties', this.applyToPropertiesCallback.bind(this));
  }

  /**
   * Apply to the properties callback.
   */
  applyToPropertiesCallback(propertyIds) {
    if (propertyIds.length === 0) {
      this.showSelectPropertiesDialog();
    } else {
      this.propertyIds = propertyIds;
      setTimeout(() => {
        this.assignTemplateToProperties(true);
      });
    }
  }

  /**
   * Removes template from the properties.
   */
  removeTemplateFromProperties() {
    const data = {
      calculationModelId: -1,
      projectId: this.projectId,
      propertyIds: this.propertyIds
    };

    this.cmService.assignCalculationModel(data).subscribe({
      next: (gpr: GenericPolluxResult) => {
        if (gpr.success) {
          this.eventBus.broadcastEvent('properties:calculationModelTemplateAssigned', this.projectId);
        }
        this.showTemplateRemovedDialog(gpr.success);
      },
      error: (_reason) => {
        this.showTemplateRemovedDialog(false);
      }
    });
  }

  /**
   * Removes form the properties.
   */
  removeFromProperties() {
    this.eventBus.broadcastEvent('properties:getCheckedProperties', this.removeFromPropertiesCallback.bind(this));
  }

  /**
   * Removes the specified property IDs from the properties callback.
   */
  removeFromPropertiesCallback(propertyIds) {
    if (propertyIds.length === 0) {
      this.showSelectPropertiesDialog();
    } else {
      this.propertyIds = propertyIds;
      setTimeout(() => {
        this.removeTemplateFromProperties();
      });
    }
  }

  /**
   * Observes the properties callback with the specified property IDs.
   * @param propertyIds - The property IDs to observe.
   */
  observePropertiesCallback(propertyIds) {
    this.propertyIds = propertyIds;
    this.ref.detectChanges();
  }

  /**
   * Shows the replace template dialog.
   */
  showReplaceTemplateDialog() {
    this.eventBus.broadcastEvent("openConfirmDialog", {
      success: () => {
        setTimeout(() => {
          this.assignTemplateToProperties(false);
        });
      },
      cancel: () => {
        // Do nothing: just to show Cancel button
      }
    }, 'applyCalculationModelTemplateReplaceTemplate');
  }

  /**
   * Shows the select properties dialog.
   */
  showSelectPropertiesDialog() {
    this.eventBus.broadcastEvent("openConfirmDialog", {
      success: () => {
        // Do nothing
      }
    }, 'applyCalculationModelTemplateNoProperties');
  }

  /**
   * Shows the template applied dialog with the specified success status.
   * @param success - Indicates whether the template application was successful or not.
   */
  showTemplateAppliedDialog(success: boolean) {
    const dialogResource = success ? 'applyCalculationModelTemplateSuccess' : 'applyCalculationModelTemplateFailed';
    this.eventBus.broadcastEvent("openConfirmDialog", {
      success: () => {
        // Do nothing
      }
    }, dialogResource);
  }

  /**
   * Shows the template removed dialog with the specified success status.
   * @param success - Indicates whether the template removal was successful or not.
   */
  showTemplateRemovedDialog(success: boolean) {
    const dialogResource = success ? 'removeCalculationModelTemplateSuccess' : 'removeCalculationModelTemplateFailed';
    this.eventBus.broadcastEvent("openConfirmDialog", {
      success: () => {
        // Do nothing
      }
    }, dialogResource);
  }
}
